import React, { useMemo } from 'react';
import { WithQueryWidgetProps } from './withQueryWidget';
import { TemplateQueryContext, useTemplateQueries } from 'routes/dashboard/DashboardPageRoot';
import { ActiveWidgetRequest, AutoWidgetQueryBehavior, WidgetQueryBehavior, WidgetRequest, WidgetRequestInfo, WidgetTemplateQueryBehavior } from 'system/widget/Widget';
import QueryWidgetAPIWrapper from './QueryWidgetAPIWrapper';
import { Query } from 'system/query/Query';
import { DashboardDocument } from 'components/resources/dashboards/DashboardDocument';
import { useDashboard } from 'routes/dashboard/Dashboard';
import { convertOldStyleWidgetRequest, isOldStyleWidgetRequest } from './WidgetCompatibilityConversion';
import { isArray } from 'lodash';
import i18n from 'translation';
import { Theme, useTheme } from '@mui/material';

function ResolveAutoWidgetQueryBehavior(storedRequest: WidgetRequest, widgetQueries: Query[], dashboard: DashboardDocument): { widgetQueryBehavior: WidgetQueryBehavior, widgetTemplateQueryBehavior: WidgetTemplateQueryBehavior } {
  const autoWidgetQueryBehavior: AutoWidgetQueryBehavior | undefined = storedRequest.autoWidgetQueryBehavior;
  let widgetQueryBehavior: WidgetQueryBehavior = storedRequest.widgetQueryBehavior || WidgetQueryBehavior.NONE;
  let widgetTemplateQueryBehavior: WidgetTemplateQueryBehavior = storedRequest.widgetTemplateQueryBehavior || WidgetTemplateQueryBehavior.NONE;

  if (autoWidgetQueryBehavior === AutoWidgetQueryBehavior.AUTO_MULTI_ALWAYS) {
    if (dashboard.template_type) {
      widgetQueryBehavior = WidgetQueryBehavior.SINGLE_INTERSECTION;
      widgetTemplateQueryBehavior = WidgetTemplateQueryBehavior.MULTI_ALWAYS;
    } else {
      widgetQueryBehavior = WidgetQueryBehavior.MULTI_REQUEST;
      widgetTemplateQueryBehavior = WidgetTemplateQueryBehavior.SINGLE_COMBINED;
    }
  } else if (autoWidgetQueryBehavior === AutoWidgetQueryBehavior.AUTO_MULTI_WHEN_ALL) {
    if (dashboard.template_type) {
      widgetQueryBehavior = WidgetQueryBehavior.SINGLE_INTERSECTION;
      widgetTemplateQueryBehavior = WidgetTemplateQueryBehavior.MULTI_WHEN_ALL;
    } else {
      if (widgetQueries.length > 1) {
        widgetQueryBehavior = WidgetQueryBehavior.MULTI_REQUEST;
      } else {
        widgetQueryBehavior = WidgetQueryBehavior.SINGLE_INTERSECTION;
      }
      widgetTemplateQueryBehavior = WidgetTemplateQueryBehavior.SINGLE_COMBINED;
    }
  } else if (autoWidgetQueryBehavior === AutoWidgetQueryBehavior.AUTO_SINGLE) {
    widgetQueryBehavior = WidgetQueryBehavior.SINGLE_INTERSECTION;
    widgetTemplateQueryBehavior = WidgetTemplateQueryBehavior.SINGLE_COMBINED;
  }
  return { widgetQueryBehavior, widgetTemplateQueryBehavior };
}

function buildActiveWidgetRequest(storedRequest:WidgetRequest, widgetQueries:Query[], templateQueriesInfo:TemplateQueryContext, dashboard:DashboardDocument, theme: Theme): ActiveWidgetRequest | ActiveWidgetRequest[] {
  if (!(widgetQueries || [])?.length && !(templateQueriesInfo?.selectedTemplateQueries || [])?.length) {
    // If there are no widget queries and no template queries, we have no requests to run
    return [];
  }

  // Validate query requirements for this request, if applicable.
  if (storedRequest.widgetQueryRequirements) {
    if (storedRequest.widgetQueryRequirements === "REQUIRES_WIDGET_QUERIES" && !(widgetQueries || [])?.length) {
      return [];
    }
    if (storedRequest.widgetQueryRequirements === "REQUIRES_TEMPLATE_QUERIES" && !(templateQueriesInfo?.selectedTemplateQueries || [])?.length) {
      return [];
    }
    if (storedRequest.widgetQueryRequirements === "REQUIRES_WIDGET_AND_TEMPLATE_QUERIES" && (!(widgetQueries || [])?.length || !(templateQueriesInfo?.selectedTemplateQueries || [])?.length)) {
      return [];
    }
  }

  const newRequest = {
    name: storedRequest.name,
    originalRequest: storedRequest,
    endpoint: storedRequest.endpoint || '',
    activeQueryIds: [],
    queryParams: storedRequest.queryParams || '',
    requestInit: storedRequest.requestInit || {},
  } as ActiveWidgetRequest;

  let newRequests: ActiveWidgetRequest[] | undefined = undefined;

  const { widgetQueryBehavior, widgetTemplateQueryBehavior } = ResolveAutoWidgetQueryBehavior(storedRequest, widgetQueries, dashboard);

  if (widgetQueryBehavior === WidgetQueryBehavior.NONE &&
    widgetTemplateQueryBehavior === WidgetTemplateQueryBehavior.NONE) {
    return newRequest;
  }

  const activeQueryIds = newRequest.activeQueryIds;

  const selectedTemplateQueryIds = templateQueriesInfo.selectedTemplateQueries?.map(q => q.id);
  const templateQueriesUnion = templateQueriesInfo.templateQueriesUnion;
  const globalQuery = templateQueriesInfo.globalQuery;

  const allQueries = [...(widgetQueries || []), ...(templateQueriesInfo.rawTemplateQueries || []), templateQueriesUnion, globalQuery];
  function getQueryById(id: string): Query | undefined {
    return allQueries.find(q => q && q.id === id);
  }

  // Using multquery for both tempalte and widget queries is not allowed
  // because it creates a combinatorial explosion of requests (would be # of widget queries * # of template queries)
  if (widgetQueryBehavior === WidgetQueryBehavior.MULTI_REQUEST && (
    widgetTemplateQueryBehavior === WidgetTemplateQueryBehavior.MULTI_ALWAYS ||
    widgetTemplateQueryBehavior === WidgetTemplateQueryBehavior.MULTI_WHEN_ALL
  )) {
    throw new Error("Cannot use MULTI_REQUEST with MULTI_ALWAYS or MULTI_WHEN_ALL template query behavior");
  }

  // First, gather the dashboard level queries
  if (widgetTemplateQueryBehavior === WidgetTemplateQueryBehavior.SINGLE_COMBINED ) {
    if (selectedTemplateQueryIds && selectedTemplateQueryIds.length > 1 && templateQueriesUnion?.id) {
      // If we have multiple selected template queries, use the union query if it exists
      activeQueryIds.push(templateQueriesUnion.id);
    } else if (selectedTemplateQueryIds && selectedTemplateQueryIds.length === 1) {
      newRequest.queryRequestColor = templateQueriesInfo.selectedTemplateQueries?.[0]?.color || undefined;
      activeQueryIds.push(...selectedTemplateQueryIds);
    }
  } else if (widgetTemplateQueryBehavior === WidgetTemplateQueryBehavior.SINGLE_INTERSECTION) {
    if (selectedTemplateQueryIds && selectedTemplateQueryIds.length > 0) {
      activeQueryIds.push(...selectedTemplateQueryIds);
      if (selectedTemplateQueryIds.length === 1) {
        newRequest.queryRequestColor = templateQueriesInfo.selectedTemplateQueries?.[0]?.color || undefined;
      }
    }
  } else if (widgetTemplateQueryBehavior === WidgetTemplateQueryBehavior.MULTI_WHEN_ALL ||
              widgetTemplateQueryBehavior === WidgetTemplateQueryBehavior.MULTI_ALWAYS) {
    let activeTemplateQueries = templateQueriesInfo.selectedTemplateQueries || [];
    if (widgetTemplateQueryBehavior === WidgetTemplateQueryBehavior.MULTI_ALWAYS) {
      activeTemplateQueries = templateQueriesInfo.allTemplateQueries || [];
    }

    // If we have more than one selected query, use those - otherwise use all.
    let activeQueries = activeTemplateQueries;
    if (templateQueriesInfo.selectedTemplateQueries && templateQueriesInfo.selectedTemplateQueries.length > 1) {
      activeQueries = templateQueriesInfo.selectedTemplateQueries || [];
    }

    // Expand to multiple requests, one for each template query
    newRequests = activeQueries.filter(q => q && q.id && typeof q.id === 'string').map(q => {
      const activeTemplateQuery = q.query;
      const queryDisplayTitle = activeTemplateQuery?.name;
      let requestInit = storedRequest.requestInit;
      if (activeTemplateQueries.length > 1 && storedRequest.multiRequestInit) {
        requestInit = storedRequest.multiRequestInit;
      }
      const templateQueryColor = templateQueriesInfo.selectedTemplateQueries.find((tq) => tq.id === q.id)?.color;
      return {
        ...newRequest,
        requestInit: requestInit,
        queryRequestColor: q.query?.meta?.color || templateQueryColor || theme.colors.namedColors.chartColorArray[activeQueries.indexOf(q) % theme.colors.namedColors.chartColorArray.length],
        activeQueryIds: [q.id],
        queryDisplayTitle: queryDisplayTitle
      } as ActiveWidgetRequest;
    });
  }

  if (!newRequests) {
    newRequests = [newRequest];
  }

  // Now, gather the widget queries
  const widgetQueryIds = widgetQueries.map(q => q.id).filter(id => typeof id === 'string');

  newRequests.forEach((request) => {
    const widgetQueryIndex = storedRequest.widgetQueryIndex;

    // If a widget query index is specified and available, it takes precedence over the widgetQueryBehavior
    if (widgetQueryIndex !== undefined && widgetQueries && widgetQueries.length > 0) {
      const widgetQuery = widgetQueries[widgetQueryIndex];
      if (widgetQuery && widgetQuery.id && typeof widgetQuery.id === 'string') {
        request.activeQueryIds.push(widgetQuery.id as string);
        request.queryDisplayTitle = widgetQuery.name;
      }
    } else if (widgetQueryBehavior === WidgetQueryBehavior.SINGLE_COMBINED) {
      // NOT YET SUPPORTED - ACTS LIKE SINGLE INTERSECTION FOR NOW
      if (widgetQueryIds && widgetQueryIds.length > 0) {
        request.activeQueryIds.push(...widgetQueryIds);
      }
    } else if (widgetQueryBehavior === WidgetQueryBehavior.SINGLE_INTERSECTION) {
      if (widgetQueryIds && widgetQueryIds.length > 0) {
        request.activeQueryIds.push(...widgetQueryIds);
      }
    } else if (widgetQueryBehavior === WidgetQueryBehavior.MULTI_REQUEST) {
      // If there are no widget queries, skip - we fall back to just the template queries
      if (!widgetQueries || widgetQueries.length === 0) {
        return;
      }

      // Expand to multiple requests, one for each widget query
      newRequests = widgetQueries.filter(q => q && q.id && typeof q.id === 'string').map((q, index) => {
        const requestInit = storedRequest.multiRequestInit || storedRequest.requestInit;
        const activeWidgetQuery = getQueryById(q.id as string);
        const newRequest = {
          ...request,
          requestInit: requestInit,
          queryRequestColor: q?.meta?.color || theme.colors.namedColors.chartColorArray[index % theme.colors.namedColors.chartColorArray.length],
          activeQueryIds: [...request.activeQueryIds, q.id],
          queryDisplayTitle: activeWidgetQuery?.name
        } as ActiveWidgetRequest;
        return newRequest;
      });
    } else if (widgetQueryBehavior === WidgetQueryBehavior.NONE) {
      // Do nothing, we are not using widget queries
    }
  });

  // Add the global query if it exists
  if (globalQuery && typeof globalQuery.id === "string") {
    newRequests.forEach((request) => {
      if (globalQuery.id !== undefined && !request.activeQueryIds.includes(globalQuery.id)) {
        request.activeQueryIds.push(globalQuery.id);
      }
    });
  }

  // Create remaining missing query titles based on the request parameters
  newRequests.filter(request => !request.queryDisplayTitle).forEach(request => {
    // Template dashboards should always prioritize the template query titles, unless the request is set to not use them
    if (dashboard.template_type && widgetTemplateQueryBehavior !== WidgetTemplateQueryBehavior.NONE && templateQueriesInfo.selectedTemplateQueries) {
      // If we have multiple selected template queries, use a title indicating that
      if (templateQueriesInfo.selectedTemplateQueries.length === templateQueriesInfo.rawTemplateQueries?.length) {
        if (templateQueriesInfo.allTemplateQueries.length === 1) {
          const firstSelectedQuery = templateQueriesInfo.selectedTemplateQueries[0];
          request.queryDisplayTitle = firstSelectedQuery.query.name;
        } else if (widgetTemplateQueryBehavior === WidgetTemplateQueryBehavior.SINGLE_COMBINED) {
          request.queryDisplayTitle = i18n.t("widgets.all-queries-combined");
        } else if (widgetTemplateQueryBehavior === WidgetTemplateQueryBehavior.SINGLE_INTERSECTION) {
          request.queryDisplayTitle = i18n.t("widgets.all-queries-intersection");
        }
      } else if (templateQueriesInfo.selectedTemplateQueries.length > 1) {
        if (widgetTemplateQueryBehavior === WidgetTemplateQueryBehavior.SINGLE_COMBINED) {
          request.queryDisplayTitle = i18n.t("widgets.selected-queries-combined");
        } else if (widgetTemplateQueryBehavior === WidgetTemplateQueryBehavior.SINGLE_INTERSECTION) {
          request.queryDisplayTitle = i18n.t("widgets.selected-queries-intersection");
        }
      } else {
        // Otherwise, use the first selected template query's name
        const firstSelectedQuery = templateQueriesInfo.selectedTemplateQueries[0];
        if (firstSelectedQuery) {
          request.queryDisplayTitle = firstSelectedQuery.query.name;
        }
      }
    // We aren't in a template dashboard, or the request is set to not use template queries,
    // So prioritize the widget query titles
    } else if (widgetQueries && widgetQueries.length > 0 && widgetQueryBehavior !== WidgetQueryBehavior.NONE) {
      if (widgetQueries.length === 1) {
        request.queryDisplayTitle = widgetQueries[0].name;
      } else if (widgetQueryBehavior === WidgetQueryBehavior.SINGLE_COMBINED) {
        request.queryDisplayTitle = i18n.t("widgets.all-queries-combined");
      } else if (widgetQueryBehavior === WidgetQueryBehavior.SINGLE_INTERSECTION) {
        request.queryDisplayTitle = i18n.t("widgets.all-queries-intersection");
      }
    }
    // If this is a fixed dashboard (with no widget query) and there is a default dashboard query set.
    else if (!dashboard.template_type && dashboard.data.defaultDashboardQuery && templateQueriesInfo.allTemplateQueries.length) {
      const found = templateQueriesInfo.allTemplateQueries.find((q) => q.id === dashboard.data.defaultDashboardQuery);
      request.queryDisplayTitle =found?.query.name;
    }
    
    if (!request.queryDisplayTitle) {
      // If we still don't have a title, use the request name as a fallback
      request.queryDisplayTitle = request.name;
    }
  });

  // Filter out any requests that have no active queries if the widget is using queries
  if (widgetQueryBehavior !== WidgetQueryBehavior.NONE ||
      widgetTemplateQueryBehavior !== WidgetTemplateQueryBehavior.NONE) {
    newRequests = newRequests.filter(request => request.activeQueryIds && request.activeQueryIds.length > 0);
  }

  return newRequests;
}

// Using the widget queries, dashboard queries, and template queries, build the widget requests
export default function QueryWidgetRequestWrapper(props: WithQueryWidgetProps): JSX.Element {
  const dashboard = useDashboard();
  const theme = useTheme();
  const templateQueriesInfo = useTemplateQueries();
  const { widget, widgetQueries, widgetRegistryInfo } = props;

  // Compile the request info for the widget, using defaults if needed
  // Also, temporarily support and convert old style widget requests
  const requestInfo = useMemo(() => {
    let requestInfo = props.widget.request_info || {
      queries: [],
      requests: []
    } as WidgetRequestInfo;

    // If there is no request info, or it looks like an old style request, try to convert it
    if (isOldStyleWidgetRequest(props.widget.request_info)) {
        const tempRequestInfo = convertOldStyleWidgetRequest(widget, widgetQueries || []);
        if (tempRequestInfo) {
          requestInfo = tempRequestInfo;
        } else {
          console.warn(`Widget "${props.widget.name}" has an old style request that could not be converted.`);
        }
    }

    if (requestInfo.requests === undefined || requestInfo.requests.length === 0) {
      if (widgetRegistryInfo.buildDefaultRequestBody && props.queryInfo) {
        const defaultRequests = widgetRegistryInfo.buildDefaultRequestBody(props.queryInfo);
        if (defaultRequests ) {
          if (isArray(defaultRequests)) {
            requestInfo.requests = defaultRequests;
          } else {
            requestInfo.requests = [defaultRequests];
          }
        }
      }
    }

    return requestInfo;
  }, [props.widget.request_info, props.widget.name, props.widget.id, props.widget, props.widget.request_info?.queries, props.widget.request_info?.requests, props.widget.request_info?.query_id, props.widget.request_info?.query_options, props.widget.request_info?.single, props.widget.request_info?.multi]);

  const activeRequests = React.useMemo(() => {
    if (!requestInfo.requests || requestInfo.requests.length === 0) {
      return [];
    }

    return requestInfo.requests.map((storedRequest: WidgetRequest) => {
      return buildActiveWidgetRequest(storedRequest, widgetQueries || [], templateQueriesInfo, dashboard, theme);
    }).filter(req => {
      if (Array.isArray(req) && req.length === 0) {
        return false;
      }
      return !!req;
    });
  }, [requestInfo, templateQueriesInfo, props.widgetQueries]);

  return (
    <>
    {activeRequests &&
      <QueryWidgetAPIWrapper
        {...props}
        activeWidgetRequests={activeRequests}
        >
      </QueryWidgetAPIWrapper>
    }
    </>
  );
}
